import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Snackbar, Typography, styled } from '@mui/material';
// import { loadCaptchaEnginge, LoadCanvasTemplateNoReload, validateCaptcha } from 'react-simple-captcha';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../../../components/Iconify';
import useLocales from '../../../../hooks/useLocales';

import useAuth from '../../../../hooks/useAuth';
import { useDispatch } from '../../../../redux/store';
import { openLoginSignup } from '../../../../redux/slices/authPages';
import palette from '../../../../theme/palette';
import { InputContainer, StyledInput, ThemeButton } from '../../../GlobalStyles';

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  textAlign: 'center',
}));

const StyledIconify = styled(Iconify)(({ theme }) => ({
  height: 24,
  width: 24,
  color: palette.visitor.icon,
}));

const ForgotPasswordText = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
  fontFamily: 'sans-serif',
  fontWeight: '900',
  color: palette.visitor.dialog.otherText,
  cursor: 'pointer',
  textDecoration: 'underline',
  margin: '8px 0px 0px',
  textAlign: 'end',
  marginBottom: theme.spacing(1),
}));

const FooterText = styled(Typography)(({ theme }) => ({
  fontSize: '0.8rem',
  fontFamily: 'sans-serif',
  fontWeight: 900,
  color: palette.visitor.dialog.footerText,
  cursor: 'pointer',
  textAlign: 'center',
}));

const LoginButton = styled(LoadingButton)(({ theme }) => ({
  width: '100%',
  marginBottom: 8,
  background: palette.visitor.login.buttonBg,
  border: `2px solid ${palette.visitor.login.border}`,
  color: palette.visitor.login.color,
}));

const _loginForm = {
  userUsername: '',
  userPassword: '',
  captcha: '',
};

const CustomHr = ({ style }) => (
  <hr style={{ borderTop: '1.5px solid black', borderBottom: 'none', height: 1, ...style }} />
);

const ErrorText = ({ children }) => (
  <span style={{ fontSize: '.75rem', color: palette.visitor.errorText }}>{children}</span>
);

const LinkText = ({ children }) => (
  <span style={{ fontWeight: 900, color: palette.visitor.link, textDecoration: 'underline' }}>{children}</span>
);

const Login = ({ setOpenForgotPasswordDialog, setActiveTab }) => {
  const { login } = useAuth();

  const dispatch = useDispatch();

  const { translate } = useLocales();

  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [loginForm, setLoginForm] = useState(_loginForm);
  const [formError, setFormError] = useState({});
  const [isEmail, setIsEmail] = useState(false);
  const [statusMessage, setStatusMessage] = useState({
    open: false,
    message: '',
    type: 'success',
  });
  const [validationErrors, setValidationErrors] = useState(null);

  // useEffect(() => {
  //   setTimeout(() => {
  //     loadCaptchaEnginge(4);
  //   }, 1000);
  // }, []);

  useEffect(() => {
    isValid(true);

    const keyDownHandler = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        onSubmit();
      }
    };

    document.addEventListener('keydown', keyDownHandler);

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [loginForm]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setLoginForm((prevState) => ({ ...prevState, [name]: value }));
  };

  const onSubmit = async () => {
    if (!isValid()) return;

    setIsLoading(true);

    const requestData = {
      'user-username': loginForm?.userUsername.trim().replace('+', ''),
      'user-password': loginForm?.userPassword.trim(),
    };

    try {
      await login(requestData);

      // close login/register dialog
      dispatch(openLoginSignup({ open: false, isLogin: true }));
    } catch (err) {
      console.log(`Error: ${err.message}`);
      const snackbarOptions = { variant: 'error', autoHideDuration: 5000 };
      enqueueSnackbar(err.message, snackbarOptions);
    } finally {
      setIsLoading(false);
    }
  };

  const isValid = (allowEmpty = false) => {
    let valid = true;
    const _error = {};

    // if (!allowEmpty && loginForm?.userUsername.trim().length < 3) {
    //   valid = false;
    //   _error.userUsername = translate('invalid_x_must_be_atleast_y_characters', { x: translate('username'), y: '3' });
    // } else if (allowEmpty && loginForm?.userUsername.trim() && loginForm?.userUsername.trim().length < 3) {
    //   valid = false;
    //   _error.userUsername = translate('invalid_x_must_be_atleast_y_characters', { x: translate('username'), y: '3' });
    // } else {
    //   _error.userUsername = '';
    // }

    if (allowEmpty && loginForm?.userUsername?.trim()) {
      const invalidPhoneNumber = validateThaiPhoneNumber(loginForm?.userUsername?.trim());
      if (invalidPhoneNumber) {
        valid = false;
        _error.userUsername = invalidPhoneNumber;
      }
    } else {
      _error.userUsername = '';
    }

    if (!allowEmpty && loginForm?.userPassword.trim().length < 6) {
      valid = false;
      _error.userPassword = translate('invalid_x_must_be_atleast_y_characters', { x: translate('password'), y: '6' });
    } else if (allowEmpty && loginForm?.userPassword.trim() && loginForm?.userPassword.trim().length < 6) {
      valid = false;
      _error.userPassword = translate('invalid_x_must_be_atleast_y_characters', { x: translate('password'), y: '6' });
    } else {
      _error.userPassword = '';
    }

    // if (validateCaptcha(loginForm?.captcha?.trim(), false) === true) {
    //   _error.userCaptcha = '';
    // } else {
    //   valid = false;
    //   _error.userCaptcha = translate('captcha_not_match');
    // }

    setFormError((prevState) => _error);

    return valid;
  };

  const validateThaiPhoneNumber = (phoneNumber) => {
    // Check for specific patterns
    const startsWith = phoneNumber.slice(0, 2);

    if (
      (startsWith === '06' && phoneNumber.length === 10) ||
      (startsWith === '08' && phoneNumber.length === 10) ||
      (startsWith === '09' && phoneNumber.length === 10)
    ) {
      return '';
    }

    return translate('must_start_with');
  };

  return (
    <Box>
      {validationErrors && (
        <Alert onClose={() => setValidationErrors(null)} severity="error" sx={{ width: '100%', my: '10px' }}>
          {
            <ul style={{ marginLeft: 10 }}>
              {validationErrors?.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          }
        </Alert>
      )}
      <Box sx={{ width: '100%', mb: '0.6rem' }}>
        <Title>{translate('login')}</Title>
        {/* <ThemeButton
          variant="contained"
          onClick={() => dispatch(openLoginSignup({ isLogin: true, open: false }))}
          sx={{ borderRadius: '0.6rem' }}
        >
          {translate('back')}
        </ThemeButton> */}
      </Box>

      {/* <Box sx={{ borderRadius: '0.4rem', backgroundColor: palette.visitor.input.bg, display: 'flex', mb: '0.6rem' }}>
        <ThemeButton
          disableFocusRipple
          onClick={() => setIsEmail(false)}
          sx={{
            background: `rgba(255,255,255, ${isEmail ? '0' : '0.1'})`,
            width: '50%',
            py: '0.6rem',
            '&:hover': { background: `rgba(255,255,255, ${isEmail ? '0' : '0.1'})` },
          }}
        >
          {translate('Mobile Number')}
        </ThemeButton>
        <ThemeButton
          disableFocusRipple
          onClick={() => setIsEmail(true)}
          sx={{
            background: `rgba(255,255,255, ${isEmail ? '0.1' : '0'})`,
            width: '50%',
            py: '0.6rem',
            '&:hover': { background: `rgba(255,255,255, ${isEmail ? '0.1' : '0'})` },
          }}
        >
          {translate('Email')}
        </ThemeButton>
      </Box> */}
      <Box sx={{ my: { xs: '1rem', sm: '2rem' } }}>
        <InputContainer direction="row">
          <StyledInput
            name="userUsername"
            type="number"
            value={loginForm?.userUsername}
            placeholder={translate('please_input_your_x', { x: translate('phone_number') })}
            onChange={(e) => handleFormChange(e)}
          />
          {/* <StyledInput
            name="userUsername"
            value={loginForm?.userUsername}
            placeholder={translate('please_input_your_x', { x: translate('username') })}
            onChange={(e) => handleFormChange(e)}
          /> */}
        </InputContainer>
        {formError?.userUsername && <ErrorText>{formError?.userUsername}</ErrorText>}
      </Box>

      <Box sx={{ mb: { xs: '1rem', sm: '2rem' } }}>
        <InputContainer direction="row" spacing={0}>
          <StyledInput
            type={showPassword ? 'text' : 'password'}
            name="userPassword"
            value={loginForm?.userPassword}
            placeholder={translate('please_input_your_x', { x: translate('password') })}
            onChange={(e) => handleFormChange(e)}
          />
          <StyledIconify
            icon={showPassword ? 'ph:eye-light' : 'ph:eye-closed-light'}
            sx={{ cursor: 'pointer' }}
            onClick={() => setShowPassword((prevState) => !prevState)}
          />
        </InputContainer>
        {formError?.userPassword && <ErrorText>{formError?.userPassword}</ErrorText>}
      </Box>

      {/* <Box sx={{ mb: '10px' }}>
        <InputContainer direction="row">
          <StyledInput
            name="captcha"
            placeholder={translate('captcha')}
            value={loginForm?.captcha}
            onChange={(e) => handleFormChange(e)}
          />
          <Box component={'span'} sx={{ height: '30px', display: 'flex', alignItems: 'center' }}>
            <LoadCanvasTemplateNoReload />
          </Box>
        </InputContainer>
        {formError?.userCaptcha && <ErrorText>{formError?.userCaptcha}</ErrorText>}
      </Box> */}

      {/* <FooterText>
        {translate('minor_note')} <span style={{ textDecoration: 'underline' }}>{translate('terms_of_service')}</span>
      </FooterText> */}

      <ForgotPasswordText
        onClick={() => {
          const snackbarOptions = { variant: 'success', autoHideDuration: 5000 };
          enqueueSnackbar(translate('contact_customer_forgot_password'), snackbarOptions);
        }}
        // onClick={() => {
        //   setOpenForgotPasswordDialog(true);
        //   dispatch(openLoginSignup({ open: false, isLogin: true }));
        // }}
      >
        {`${translate('forgot_password')}?`}
      </ForgotPasswordText>

      <ThemeButton
        onClick={() => onSubmit()}
        fullWidth
        loading={isLoading}
        sx={{ background: 'linear-gradient(180deg, #FFE11E 45.67%, #F19119 56.25%)', borderRadius: 0.5 }}
      >
        {translate('login')}
      </ThemeButton>

      <FooterText
        onClick={() => dispatch(openLoginSignup({ open: true, isLogin: false }))}
        sx={{
          mt: 2,
        }}
      >
        {translate('new_to_x', { x: 'Yum998' })}? <LinkText>{translate('create_an_account')}</LinkText>
      </FooterText>
      {statusMessage?.open && (
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={statusMessage.open}
          autoHideDuration={3000}
          sx={{ width: 'fit-content' }}
          onClose={() => setStatusMessage((prevState) => ({ ...prevState, open: false }))}
        >
          <Alert
            onClose={() => setStatusMessage((prevState) => ({ ...prevState, open: false }))}
            severity={statusMessage.type}
            sx={{ width: '100%' }}
          >
            {statusMessage.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

Login.propTypes = {
  setOpenForgotPasswordDialog: PropTypes.func,
  setActiveTab: PropTypes.func,
};

export default Login;
